/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CommonInputSwitch.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import PropTypes from "prop-types";

export default function CommonInputSwitch({
  checked,
  onChange,
  label,
  required,
  disabled,
  error,
  ...rest
}) {
  const GREEN = "#26890D";
  const GREY = "#53565A";
  const GREY_DISABLED = "#E0E0E0";
  const GREEN_DISABLED = "#B6D8A8";
  const RED = "#C92B1C";

  let barColor = checked ? GREEN : GREY;
  let asteriskColor = RED;

  if (disabled && checked) {
    barColor = GREEN_DISABLED;
    asteriskColor = "#B0B3B8";
  } else if (disabled) {
    barColor = GREY_DISABLED;
    asteriskColor = "#B0B3B8";
  } else if (error) {
    barColor = RED;
  } else if (checked) {
    barColor = GREEN;
  }

  const BAR_LENGTH = 25;
  const BAR_HEIGHT = 8;
  const SWITCH_WIDTH = 50;
  const SWITCH_HEIGHT = 26;
  const KNOB_SIZE = 20;
  const KNOB_MARGIN = 3;
  const DOT_SIZE = 8;

  return (
    <label
      style={{
        display: "inline-flex",
        alignItems: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.7 : 1,
        gap: label ? 8 : 0,
        fontSize: "14px",
        userSelect: "none",
        fontStyle: "Open Sans",
      }}
      {...rest}
    >
      {label && (
        <span
          style={{
            color: "#848587",
            fontWeight: 500,
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            fontStyle: "Open Sans",
          }}
        >
          {label}
          {required && <span style={{ color: asteriskColor }}>*</span>}
        </span>
      )}
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        style={{
          position: "absolute",
          opacity: 0,
          width: SWITCH_WIDTH,
          height: SWITCH_HEIGHT,
          cursor: disabled ? "not-allowed" : "pointer",
          zIndex: 2,
        }}
        onChange={(e) => {
          if (disabled) return;
          onChange?.(e.target.checked);
        }}
        aria-checked={checked}
        aria-disabled={disabled}
        aria-invalid={error}
        aria-label={label}
      />
      <span
        style={{
          display: "inline-block",
          position: "relative",
          width: SWITCH_WIDTH,
          height: SWITCH_HEIGHT,
        }}
      >
        <svg
          width={SWITCH_WIDTH}
          height={SWITCH_HEIGHT}
          style={{ position: "absolute", left: 0, top: 0, zIndex: 0 }}
        >
          <rect
            x={(SWITCH_WIDTH - BAR_LENGTH) / 2}
            y={SWITCH_HEIGHT / 2 - BAR_HEIGHT / 2}
            width={BAR_LENGTH}
            height={BAR_HEIGHT}
            fill={barColor}
          />
        </svg>
        <svg
          width={KNOB_SIZE}
          height={KNOB_SIZE}
          style={{
            position: "absolute",
            left: checked
              ? SWITCH_WIDTH - KNOB_SIZE - KNOB_MARGIN
              : KNOB_MARGIN,
            top: KNOB_MARGIN,
            zIndex: 1,
            transition: "left 0.2s ease",
          }}
        >
          <circle
            cx={KNOB_SIZE / 2}
            cy={KNOB_SIZE / 2}
            r={KNOB_SIZE / 2 - 1}
            fill={checked ? GREEN : "#fff"}
            stroke={checked ? GREEN : GREY}
            strokeWidth="2"
            style={{ transition: "fill 0.2s, stroke 0.2s" }}
          />
          {checked && (
            <circle
              cx={KNOB_SIZE / 2}
              cy={KNOB_SIZE / 2}
              r={DOT_SIZE / 2}
              fill="#fff"
            />
          )}
        </svg>
      </span>
    </label>
  );
}

CommonInputSwitch.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
};


